import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../store/AuthContext';
import { useLanguage } from '../store/LanguageContext';
import {
  API_URL,
  resellerGetProfile, resellerUpdateProfile,
  resellerGetProducts,
  resellerGetCustomers, resellerCreateCustomer,
  resellerGetQuotes, resellerUpdateQuoteStatus,
  resellerCreateQuote,
  type ResellerProfile, type ResellerProduct, type ResellerCustomer, type ResellerQuote,
} from '../lib/api';
import {
  Package, Users, FileText, UserCircle as ProfileIcon,
  Save, Edit3, RefreshCw, AlertCircle, Loader2, Plus, X,
} from 'lucide-react';
import PasswordSettings from '../components/PasswordSettings';

// v3.2.47: 'customers' en 'quotes' tabs gemerged in 'klanten-offertes'
// (zoals Filip's admin dashboard) — net zo handig en consistent.
type TabId = 'profile' | 'catalog' | 'klanten-offertes';

const formatEuro = (n: number) => `€${(n ?? 0).toLocaleString('nl-BE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const formatDate = (ts: number) => new Date(ts * 1000).toLocaleDateString('nl-BE', { year: 'numeric', month: 'short', day: 'numeric' });

function StatusBadge({ status }: { status: string }) {
  const { t } = useLanguage();
  const cls = {
    draft:    'bg-gray-100 text-gray-600',
    sent:     'bg-blue-50 text-blue-700',
    accepted: 'bg-green-50 text-green-700',
    rejected: 'bg-red-50 text-red-700',
    expired:  'bg-yellow-50 text-yellow-700',
  }[status] ?? 'bg-gray-100 text-gray-600';
  return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}>{t(`admin.status.${status}`)}</span>;
}

export default function ResellerDashboard() {
  const { user, authLoading } = useAuth();
  const { t } = useLanguage();

  // v3.2.97 — Klanten & Offertes is dé hoofdtaak van een reseller, dus
  // openen we daar standaard op. Profiel + catalog blijven 1 klik weg.
  // v5.27: deep-link naar een tab via ?tab=... (voor de reviewlijst).
  const [activeTab, setActiveTab] = useState<TabId>(() => {
    const t = new URLSearchParams(window.location.search).get('tab');
    return (['profile', 'catalog', 'klanten-offertes'] as TabId[]).includes(t as TabId)
      ? (t as TabId) : 'klanten-offertes';
  });
  const [profile, setProfile] = useState<ResellerProfile | null>(null);
  const [products, setProducts] = useState<ResellerProduct[]>([]);
  const [customers, setCustomers] = useState<ResellerCustomer[]>([]);
  const [quotes, setQuotes] = useState<ResellerQuote[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [p, prods, custs, qs] = await Promise.all([
        resellerGetProfile(),
        resellerGetProducts(),
        resellerGetCustomers(),
        resellerGetQuotes(),
      ]);
      setProfile(p);
      setProducts(prods);
      setCustomers(custs);
      setQuotes(qs);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  // Superadmin gets a "preview" pass-through so Franky can test the reseller
  // dashboard without needing a separate reseller login. Owner/reseller use
  // the page normally.
  // V5.30: owner (Filip) krijgt dezelfde preview-doorgang als superadmin —
  // hij kijkt dan mee als de eerste actieve reseller.
  const isPreview = user?.role === 'superadmin' || user?.role === 'owner';
  const allowed = !!user && (user.role === 'reseller' || isPreview);
  if (!allowed) {
    // Sessie wordt nog hersteld: even niets tonen i.p.v. wegsturen.
    if (authLoading) return null;
    // Niet ingelogd → naar de loginpagina en daarna hierheen terug.
    if (!user) {
      window.location.replace(`/login?redirect=${encodeURIComponent('/reseller-dashboard' + window.location.search)}`);
      return null;
    }
    // Wel ingelogd maar als (test)klant: leg de terugweg uit.
    return (
      <div className="pt-20 max-w-3xl mx-auto px-4">
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 text-center">
          <AlertCircle className="mx-auto mb-2 text-amber-600" size={32} />
          <p className="text-amber-900 font-semibold">{t('reseller.accessDenied')}</p>
          <p className="text-amber-800 text-sm mt-1">
            Je bent nu ingelogd als klant. Klik bovenaan op "Terug naar admin" of log in met een reseller-account om deze werkplek te zien.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-20 pb-12 max-w-7xl mx-auto px-4 sm:px-6">
      {isPreview && (
        <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 px-4 py-2.5 text-sm text-blue-800">
          Voorbeeldweergave als eigenaar — je kijkt mee met <b>{profile?.company_name ?? 'de eerste reseller'}</b>. Via "Test als…" bovenaan log je écht in als een handelaar.
        </div>
      )}
      {/* Gsm-compact (Franky 19/8) */}
      <header className="mb-3 sm:mb-6 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-lg sm:text-2xl font-bold text-[#1a1a1a]" style={{ fontFamily: 'Playfair Display' }}>
            {t('reseller.title')}
          </h1>
          {/* V5.31: duidelijk verschil hoofdaccount vs subaccount (medewerker) */}
          {profile && !isPreview && (
            <span className={`inline-block mb-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide border ${
              profile.user_id === user.id
                ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                : 'bg-blue-50 text-blue-700 border-blue-200'
            }`}>
              {profile.user_id === user.id ? 'Hoofdaccount' : `Subaccount — ${user.name}`}
            </span>
          )}
          <p className="text-xs sm:text-sm text-[#8a7e74] mt-0.5 sm:mt-1 truncate">
            {profile?.company_name ?? user.name}<span className="hidden sm:inline"> — {t('reseller.subtitle')}</span>
          </p>
        </div>
        <button
          onClick={load}
          className="flex items-center gap-2 px-3 py-2 text-xs bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          disabled={loading}
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          <span className="hidden sm:inline">{t('admin.refresh')}</span>
        </button>
      </header>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800 flex items-center gap-2">
          <AlertCircle size={16} /> {error}
        </div>
      )}

      {/* Tabs */}
      <nav className="flex gap-1 mb-3 sm:mb-6 border-b border-gray-200 overflow-x-auto">
        {([
          // v3.2.97 — Volgorde: Klanten/Offertes vooraan (kernactiviteit),
          // dan Catalogus (referentie), Profiel achteraan (zelden nodig).
          { id: 'klanten-offertes' as TabId, icon: Users,       label: 'Klanten & Offertes' },
          { id: 'catalog' as TabId,          icon: Package,     label: t('reseller.tab.catalog') },
          { id: 'profile' as TabId,          icon: ProfileIcon, label: t('reseller.tab.profile') },
        ]).map(tab => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-4 py-2 sm:py-2.5 text-xs sm:text-sm font-medium whitespace-nowrap transition-colors ${
                isActive
                  ? 'text-[#c47a3a] border-b-2 border-[#c47a3a] -mb-px'
                  : 'text-[#8a7e74] hover:text-[#1a1a1a]'
              }`}
            >
              <Icon size={16} />
              {tab.label}
            </button>
          );
        })}
      </nav>

      {loading && !profile ? (
        <div className="flex items-center justify-center py-20 text-[#8a7e74]">
          <Loader2 className="animate-spin mr-2" size={20} />
          {t('reseller.loading')}
        </div>
      ) : (
        <>
          {activeTab === 'profile' && profile && (
            <div className="space-y-4">
              <><ProfileTab profile={profile} onSaved={load} /><MedewerkersPanel /></>
              {/* v5 blok 7: paswoord instellen — handelaars horen met een
                   paswoord in te loggen (winkelmailbox is gedeeld). */}
              <PasswordSettings />
            </div>
          )}
          {activeTab === 'catalog' && <CatalogTab products={products} />}
          {activeTab === 'klanten-offertes' && (
            <KlantenOffertesTab profile={profile} customers={customers} quotes={quotes} products={products} onRefresh={load} />
          )}
        </>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// PROFILE TAB
// ════════════════════════════════════════════════════════════════════════════

// ════════════════════════════════════════════════════════════════════════════
// V6.24 — MIJN MEDEWERKERS
// De handelaar geeft zelf collega's een eigen login, zonder tussenkomst van
// het Gevelsteenbedrijf. Enkel het hoofdaccount ziet dit blok; een medewerker
// krijgt van de server een 403 en dan tonen we het blok gewoon niet.
// ════════════════════════════════════════════════════════════════════════════

function MedewerkersPanel() {
  const [leden, setLeden] = useState<import('../lib/api').ResellerMedewerker[]>([]);
  const [mag, setMag] = useState(true);
  const [laden, setLaden] = useState(true);
  const [open, setOpen] = useState(false);
  const [bezig, setBezig] = useState(false);
  const [fout, setFout] = useState<string | null>(null);
  const [form, setForm] = useState({ name: '', email: '', password: '' });

  const laad = async () => {
    setLaden(true);
    try {
      const api = await import('../lib/api');
      setLeden(await api.resellerGetMembers());
      setMag(true);
    } catch (e) {
      if ((e as { status?: number }).status === 403) setMag(false);
      else setFout((e as Error).message);
    } finally { setLaden(false); }
  };
  useEffect(() => { void laad(); }, []);

  const bewaar = async () => {
    setFout(null);
    if (form.name.trim().length < 2) { setFout('Vul de naam van uw medewerker in.'); return; }
    if (!/.+@.+\..+/.test(form.email)) { setFout('Vul een geldig e-mailadres in.'); return; }
    if (form.password.length < 8) { setFout('Kies een paswoord van minstens 8 tekens.'); return; }
    setBezig(true);
    try {
      const api = await import('../lib/api');
      await api.resellerCreateMember({ name: form.name.trim(), email: form.email.trim().toLowerCase(), password: form.password });
      setForm({ name: '', email: '', password: '' });
      setOpen(false);
      await laad();
    } catch (e) { setFout((e as Error).message); }
    finally { setBezig(false); }
  };

  const zetActief = async (id: string, actief: boolean) => {
    try {
      const api = await import('../lib/api');
      await api.resellerUpdateMember(id, { is_active: actief ? 1 : 0 });
      await laad();
    } catch (e) { alert((e as Error).message); }
  };

  if (!mag || (laden && leden.length === 0)) return null;

  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden mt-6">
      <div className="px-4 sm:px-6 py-4 border-b border-gray-100 flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <h3 className="text-base font-bold text-[#1a1a1a] flex items-center gap-2">
            👥 Mijn medewerkers
            <span className="px-2 py-0.5 bg-[#c47a3a]/10 text-[#c47a3a] text-xs font-semibold rounded-full">{leden.length}</span>
          </h3>
          <p className="text-xs text-[#8a7e74] mt-0.5">
            Geef een collega een eigen login. Hij ziet dezelfde offertes en bestellingen als u,
            maar kan zelf geen accounts aanmaken.
          </p>
        </div>
        <button onClick={() => setOpen(o => !o)}
          className="px-4 py-2 bg-[#c47a3a] hover:bg-[#a8643a] text-white rounded-lg text-sm font-bold flex-shrink-0">
          {open ? 'Annuleren' : '+ Medewerker toevoegen'}
        </button>
      </div>

      {open && (
        <div className="px-4 sm:px-6 py-4 bg-[#faf7f3] border-b border-gray-100">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              placeholder="Naam van de medewerker"
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white min-w-0" />
            <input value={form.email} type="email" onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
              placeholder="E-mailadres"
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white min-w-0" />
            <input value={form.password} type="text" onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
              placeholder="Paswoord (min. 8 tekens)"
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white min-w-0" />
          </div>
          {fout && <p className="mt-2 text-sm text-red-600">{fout}</p>}
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <button onClick={() => void bewaar()} disabled={bezig}
              className="px-5 py-2 bg-[#c47a3a] text-white rounded-lg text-sm font-bold disabled:opacity-50">
              {bezig ? 'Bezig…' : 'Account aanmaken'}
            </button>
            <span className="text-xs text-[#8a7e74]">
              Uw medewerker krijgt een mail dat zijn toegang klaarstaat; het paswoord geeft u zelf door.
            </span>
          </div>
        </div>
      )}

      <div className="px-4 sm:px-6 py-4">
        {leden.length === 0 ? (
          <p className="text-sm text-[#8a7e74]">Nog geen medewerkers — u werkt alleen op dit account.</p>
        ) : (
          <div className="space-y-2">
            {leden.map(m => (
              <div key={m.id} className="flex flex-wrap items-center justify-between gap-2 border border-gray-100 rounded-lg px-3 py-2">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-[#1a1a1a] truncate">{m.name}</p>
                  <p className="text-xs text-[#8a7e74] truncate">{m.email}{m.vestiging_naam ? ` · ${m.vestiging_naam}` : ''}</p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className={`px-2 py-0.5 rounded text-xs font-bold ${m.is_active ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
                    {m.is_active ? 'actief' : 'uitgeschakeld'}
                  </span>
                  <button onClick={() => void zetActief(m.id, !m.is_active)}
                    className="px-2.5 py-1 border border-gray-200 hover:border-[#c47a3a] rounded-lg text-xs font-semibold text-[#8a7e74] hover:text-[#c47a3a]">
                    {m.is_active ? 'Uitschakelen' : 'Weer inschakelen'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ProfileTab({ profile, onSaved }: { profile: ResellerProfile; onSaved: () => void }) {
  const { t } = useLanguage();
  // V6 (Franky 19/8): de handelaar ziet zijn eigen installateurs — beheerd
  // door Filip in het reseller-dossier (installers_json).
  const mijnInstallateurs: Array<{ company_name: string; contact_name?: string | null; phone?: string | null; city?: string | null }> = (() => {
    try { return profile.installers_json ? JSON.parse(profile.installers_json) : []; } catch { return []; }
  })();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [form, setForm] = useState({
    contact_email: profile.contact_email ?? '',
    contact_phone: profile.contact_phone ?? '',
    address:       profile.address ?? '',
    postal_code:   profile.postal_code ?? '',
    city:          profile.city ?? '',
    notes:         profile.notes ?? '',
    // v5 blok 7: eigen logo — komt op de geprinte offerte van de reseller.
    logo_url:      profile.logo_url ?? '',
  });

  const save = async () => {
    setSaving(true);
    try {
      await resellerUpdateProfile({ ...form, logo_url: form.logo_url.trim() || null });
      setEditing(false);
      onSaved();
    } catch (e) {
      alert((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden max-w-full">
      <div className="px-4 sm:px-6 py-4 border-b border-gray-100 flex items-center justify-between">
        <h2 className="text-lg font-bold text-[#1a1a1a] flex items-center gap-2">
          <ProfileIcon size={18} className="text-[#c47a3a]" /> {t('reseller.profile.title')}
        </h2>
        {!editing && (
          <button
            onClick={() => setEditing(true)}
            className="flex items-center gap-2 px-3 py-1.5 text-xs bg-[#c47a3a] text-white rounded-lg hover:bg-[#a8643a] transition-colors"
          >
            <Edit3 size={12} /> {t('admin.profile.edit')}
          </button>
        )}
      </div>
      <div className="px-4 sm:px-6 py-6">
        {/* Read-only owner-controlled fields (reseller cannot change these) */}
        <div className="mb-6 p-4 bg-[#faf7f3] rounded-lg">
          <p className="text-xs font-semibold text-[#8a7e74] uppercase tracking-wide mb-2">{t('reseller.profile.ownerControlled')}</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 text-sm">
            <InfoRow label={t('reseller.profile.companyName')} value={profile.company_name} />
            <InfoRow label={t('reseller.profile.btwNumber')}   value={profile.btw_number ?? '—'} />
            <InfoRow label={t('reseller.profile.discount')}    value={`${profile.default_discount_pct}%`} />
            {mijnInstallateurs.length > 0 && (
              <div className="sm:col-span-2 mt-2 rounded-lg border border-violet-200 bg-violet-50/50 p-3">
                <p className="text-[11px] font-bold text-violet-800 uppercase tracking-wide mb-1.5">🔨 Mijn installateurs</p>
                {mijnInstallateurs.map((inst, i) => (
                  <p key={i} className="text-sm text-[#1a1a1a]">
                    <b>{inst.company_name}</b>
                    <span className="text-[#8a7e74]">{inst.contact_name ? ` — ${inst.contact_name}` : ''}{inst.city ? ` · ${inst.city}` : ''}{inst.phone ? ` · ${inst.phone}` : ''}</span>
                  </p>
                ))}
                <p className="text-[10px] text-[#8a7e74] mt-1">Beheerd door het Gevelsteenbedrijf — vraag aanpassingen aan de eigenaar.</p>
              </div>
            )}
            <InfoRow label={t('reseller.profile.postcodes')}   value={profile.postcodes ?? '—'} />
          </div>
          <p className="text-[10px] text-[#8a7e74] mt-2 italic">{t('reseller.profile.ownerControlledNote')}</p>
        </div>

        {/* Self-editable fields */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label={t('reseller.profile.contactEmail')} type="email" value={form.contact_email} editing={editing}
            onChange={v => setForm(f => ({ ...f, contact_email: v }))} />
          <Field label={t('reseller.profile.contactPhone')} value={form.contact_phone} editing={editing}
            onChange={v => setForm(f => ({ ...f, contact_phone: v }))} />
          <Field label={t('reseller.profile.address')} value={form.address} editing={editing}
            onChange={v => setForm(f => ({ ...f, address: v }))} />
          <Field label={t('reseller.profile.city')} value={form.city} editing={editing}
            onChange={v => setForm(f => ({ ...f, city: v }))} />
          <Field label={t('reseller.profile.postalCode')} value={form.postal_code} editing={editing}
            onChange={v => setForm(f => ({ ...f, postal_code: v }))} />
          {/* v5 blok 7: eigen logo — komt bovenaan op uw geprinte offertes */}
          <Field label="Logo-URL (op uw offertes)" value={form.logo_url} editing={editing}
            onChange={v => setForm(f => ({ ...f, logo_url: v }))} />
        </div>
        {form.logo_url && (
          <div className="mt-3 flex items-center gap-2">
            <span className="text-xs text-[#8a7e74]">Uw logo:</span>
            <img src={form.logo_url} alt="logo" className="h-12 w-auto max-w-[220px] border border-gray-100 rounded p-1 bg-white" onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
          </div>
        )}

        {/* V5.34: standaardlogo kiezen (gegenereerd op de bedrijfsnaam) of
             eigen logo opladen. Komt in het dashboard én op offerte/factuur. */}
        <div className="mt-4 border-t border-gray-100 pt-4">
          <p className="text-xs font-bold text-[#8a7e74] uppercase tracking-wide mb-2">Kies een standaardlogo of laad uw eigen logo op</p>
          <div className="flex flex-wrap gap-2">
            {[1, 2, 3, 4, 5, 6].map(st => {
              const url = `${API_URL}/api/logo.svg?name=${encodeURIComponent(profile.company_name)}&style=${st}`;
              const active = form.logo_url === url;
              return (
                <button
                  key={st}
                  onClick={async () => {
                    setForm(f => ({ ...f, logo_url: url }));
                    await resellerUpdateProfile({ logo_url: url });
                    onSaved?.();
                  }}
                  className={`h-14 px-2 bg-white rounded-lg border-2 transition-all ${active ? 'border-[#c47a3a]' : 'border-gray-200 hover:border-gray-300'}`}
                  title={`Stijl ${st}`}
                >
                  <img src={url} alt={`Logostijl ${st}`} className="h-10 w-auto max-w-[170px]" />
                </button>
              );
            })}
            <label className="h-14 px-4 flex items-center gap-2 bg-[#faf7f3] rounded-lg border-2 border-dashed border-gray-300 hover:border-[#c47a3a] cursor-pointer text-xs font-semibold text-[#4a4a4a]">
              {logoUploading ? <Loader2 size={14} className="animate-spin" /> : '⬆'} Eigen logo opladen
              <input
                type="file" accept="image/*" className="hidden"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  setLogoUploading(true);
                  try {
                    const fd = new FormData();
                    fd.append('file', file);
                    const token = localStorage.getItem('gevel_session_token');
                    const r = await fetch(`${API_URL}/api/uploads/reseller-logo`, {
                      method: 'POST', body: fd,
                      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
                    });
                    const j = await r.json();
                    if (!r.ok || !j.ok) throw new Error(j.error || 'Upload mislukt');
                    setForm(f => ({ ...f, logo_url: j.data.url }));
                    onSaved?.();
                  } catch (err) {
                    alert((err as Error).message);
                  } finally {
                    setLogoUploading(false);
                    e.target.value = '';
                  }
                }}
              />
            </label>
          </div>
        </div>

        {editing && (
          <div className="mt-5 flex gap-2">
            <button
              onClick={save}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-[#c47a3a] text-white text-sm font-semibold rounded-lg hover:bg-[#a8643a] disabled:opacity-50 transition-colors"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              {saving ? t('admin.profile.saving') : t('admin.profile.save')}
            </button>
            <button
              onClick={() => setEditing(false)}
              className="px-4 py-2 text-sm font-medium text-[#4a4a4a] bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              {t('admin.profile.cancel')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline gap-3">
      <span className="text-xs font-semibold text-[#8a7e74] uppercase tracking-wide min-w-[120px]">{label}</span>
      <span className="text-sm text-[#1a1a1a]">{value}</span>
    </div>
  );
}

function Field({ label, value, editing, onChange, type = 'text' }: {
  label: string; value: string; editing: boolean;
  onChange: (v: string) => void; type?: string;
}) {
  return (
    <div>
      <label className="block text-xs font-semibold text-[#8a7e74] uppercase tracking-wide mb-1">{label}</label>
      {editing ? (
        <input
          type={type}
          value={value}
          onChange={e => onChange(e.target.value)}
          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#c47a3a]"
        />
      ) : (
        <p className="text-sm text-[#1a1a1a] py-2">{value || '—'}</p>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// CATALOG TAB
// ════════════════════════════════════════════════════════════════════════════

function CatalogTab({ products }: { products: ResellerProduct[] }) {
  const { t } = useLanguage();
  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden max-w-full">
      <div className="px-4 sm:px-6 py-4 border-b border-gray-100">
        <h2 className="text-lg font-bold text-[#1a1a1a] flex items-center gap-2">
          <Package size={18} className="text-[#c47a3a]" /> {t('reseller.catalog.title')}
          <span className="px-2 py-0.5 bg-[#c47a3a]/10 text-[#c47a3a] text-xs font-semibold rounded-full">{products.length}</span>
        </h2>
        <p className="text-xs text-[#8a7e74] mt-1">{t('reseller.catalog.desc')}</p>
      </div>
      <div className="px-4 sm:px-6 py-4">
        {/* V6 punt 16: nog geen korting toegekend -> publieke prijs + oproep */}
        {products.length > 0 && products.every(p => p.effective_price >= p.catalog_price) && (
          <div className="mb-3 bg-violet-50 border border-violet-200 rounded-lg px-3 py-2.5 text-sm text-violet-900">
            U ziet momenteel de <strong>publieke catalogusprijs</strong> — er is nog geen
            korting aan uw account gekoppeld. <strong>Contacteer ons voor uw korting.</strong>
          </div>
        )}
        {products.length === 0 ? (
          <p className="text-sm text-[#8a7e74] py-4">{t('reseller.catalog.empty')}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100">
                  {[t('reseller.catalog.product'), t('reseller.catalog.color'), t('reseller.catalog.category'), t('reseller.catalog.catalogPrice'), t('reseller.catalog.yourPrice'), t('reseller.catalog.discount')].map(h => (
                    <th key={h} className="text-left py-2 pr-4 text-xs font-semibold text-[#8a7e74] uppercase tracking-wide">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {products.map(p => {
                  const discountPct = p.catalog_price > 0
                    ? Math.round((1 - p.effective_price / p.catalog_price) * 100)
                    : 0;
                  return (
                    <tr key={p.id} className="border-b border-gray-50 hover:bg-[#faf7f3] transition-colors">
                      <td className="py-2.5 pr-4 font-medium text-[#1a1a1a]">
                        {p.name}
                        {p.is_remnant === 1 && (
                          <span className="ml-2 px-1.5 py-0.5 bg-amber-100 text-amber-700 text-[10px] rounded">{t('reseller.catalog.remnant')}</span>
                        )}
                      </td>
                      <td className="py-2.5 pr-4 text-[#8a7e74]">
                        <span className="inline-block w-3 h-3 rounded-full mr-1 align-middle" style={{ backgroundColor: p.color_hex }} />
                        {p.color}
                      </td>
                      <td className="py-2.5 pr-4 text-[#8a7e74] capitalize">{p.category}</td>
                      <td className="py-2.5 pr-4 text-[#8a7e74]">{formatEuro(p.catalog_price)}/m²</td>
                      <td className="py-2.5 pr-4 font-bold text-[#c47a3a]">{formatEuro(p.effective_price)}/m²</td>
                      <td className="py-2.5 pr-4 text-green-700">{discountPct > 0 ? `−${discountPct}%` : '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// KLANTEN & OFFERTES TAB (v3.2.47 — gemerged zoals admin "Klanten & Offertes")
// ────────────────────────────────────────────────────────────────────────────
// Eén overzicht met klantenkaarten — uitgeklapt zie je alle offertes van die
// klant, met PDF-download per offerte. Reseller ziet exact dezelfde data als
// Filip in z'n backend, maar enkel zijn eigen klanten. "Orphan quotes" (offerte
// zonder gekoppelde klant — kan voorkomen bij quick-quotes vanaf de site)
// staan onderaan in een aparte sectie.
// ════════════════════════════════════════════════════════════════════════════

function KlantenOffertesTab({
  profile, customers, quotes, products, onRefresh,
}: {
  profile: ResellerProfile | null;
  customers: ResellerCustomer[];
  quotes: ResellerQuote[];
  products: ResellerProduct[];
  onRefresh: () => void;
}) {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [creatingCustomer, setCreatingCustomer] = useState(false);
  const [creatingQuote, setCreatingQuote] = useState(false);
  // v5.19: nieuwe offerte samenstellen (meerdere stenen, klant betaalt
  // catalogusprijs; de reseller ziet per regel ook zijn eigen netto).
  const [building, setBuilding] = useState(false);
  const [buildName, setBuildName] = useState('');
  const [buildEmail, setBuildEmail] = useState('');
  // V5.35: toebehoren per steen in de visuele bouwer — zelfde keuzes als de
  // calculator vooraan; de server rekent de bedragen met Filips prijzen.
  type BuildLine = {
    product_id: string; qty_m2: number;
    joint_width_mm: number; corner_count: number;
    include_glue: boolean; include_grout: boolean; grout_color: string;
  };
  const [buildLines, setBuildLines] = useState<BuildLine[]>([]);
  const [buildBusy, setBuildBusy] = useState(false);
  // v5.19: eigen bestellingen & facturen
  const [rOrders, setROrders] = useState<import('../lib/api').AdminOrder[]>([]);
  const [rInvoices, setRInvoices] = useState<import('../lib/api').AdminInvoice[]>([]);
  const [docBusy, setDocBusy] = useState<string | null>(null);
  useEffect(() => {
    void (async () => {
      try {
        const api = await import('../lib/api');
        const [o, i] = await Promise.all([api.resellerGetOrders(), api.resellerGetInvoices()]);
        setROrders(o); setRInvoices(i);
      } catch { /* leeg */ }
    })();
  }, [quotes.length]);

  // v5.26: builder start LEEG — geen voorgeselecteerde steen (wens Franky).
  const openBuilder = () => setBuilding(b => !b);
  // v5.20: offerte starten vanuit een specifieke klant — naam/e-mail
  // voorgevuld, prijzen blijven de door Filip toegekende catalogus/netto.
  const openBuilderFor = (name: string, email: string) => {
    setBuildName(name);
    setBuildEmail(email);
    setBuilding(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const buildCatalogTotal = buildLines.reduce((sum, l) => {
    const pr = products.find(p => p.id === l.product_id);
    return sum + (pr ? pr.catalog_price * l.qty_m2 : 0);
  }, 0);
  const buildNettoTotal = buildLines.reduce((sum, l) => {
    const pr = products.find(p => p.id === l.product_id);
    return sum + (pr ? pr.effective_price * l.qty_m2 : 0);
  }, 0);
  const submitBuild = async () => {
    if (!buildName.trim() || !buildEmail.trim() || buildLines.length === 0) { alert('Vul klantnaam en e-mail in.'); return; }
    setBuildBusy(true);
    try {
      const { resellerCreateQuote } = await import('../lib/api');
      await resellerCreateQuote({
        customer_name: buildName.trim(),
        customer_email: buildEmail.trim(),
        items: buildLines.map(l => ({
          product_id: l.product_id, qty_m2: l.qty_m2,
          joint_width_mm: l.joint_width_mm, corner_count: l.corner_count,
          include_glue: l.include_glue, include_grout: l.include_grout, grout_color: l.grout_color,
        })),
      } as import('../lib/api').ResellerCreateQuotePayload);
      setBuilding(false); setBuildLines([]); setBuildName(''); setBuildEmail('');
      alert('Offerte aangemaakt — de klant betaalt de catalogusprijs; uw korting is uw marge.');
      onRefresh();
    } catch (e) { alert((e as Error).message); } finally { setBuildBusy(false); }
  };
  const docAct = async (key: string, fn: () => Promise<unknown>) => {
    setDocBusy(key);
    try {
      await fn();
      const api = await import('../lib/api');
      const [o, i] = await Promise.all([api.resellerGetOrders(), api.resellerGetInvoices()]);
      setROrders(o); setRInvoices(i);
      onRefresh();
    } catch (e) { alert((e as Error).message); } finally { setDocBusy(null); }
  };
  const resellerInvoicePdf = async (inv: import('../lib/api').AdminInvoice) => {
    const win = window.open('', '_blank');
    const { printInvoicePDF } = await import('../lib/invoicePdf');
    const { OWNER_COMPANY } = await import('../lib/quotePdf');
    printInvoicePDF(inv, {
      targetWindow: win,
      orderNumber: inv.order_number ?? null,
      // Factuur draagt de branding van de reseller (eigen naam/adres/logo).
      company: {
        name: profile?.company_name ?? OWNER_COMPANY.name,
        address: profile?.address ?? '', postalCode: profile?.postal_code ?? '',
        city: profile?.city ?? '', country: 'België', btw: profile?.btw_number ?? '',
        email: profile?.contact_email ?? '', phone: profile?.contact_phone ?? '',
        logo_url: profile?.logo_url ?? undefined,
      },
    });
  };

  // v5 blok 5: offerte van een eindklant overnemen op eigen naam
  const [claimRef, setClaimRef] = useState('');
  const [claiming, setClaiming] = useState(false);
  const claimQuote = async () => {
    const ref = claimRef.trim();
    if (!ref) return;
    setClaiming(true);
    try {
      const { resellerClaimQuote } = await import('../lib/api');
      await resellerClaimQuote(ref);
      setClaimRef('');
      alert('Offerte overgenomen — ze staat nu op uw naam.');
      onRefresh();
    } catch (e) {
      alert((e as Error).message);
    } finally {
      setClaiming(false);
    }
  };
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [updatingQuoteId, setUpdatingQuoteId] = useState<string | null>(null);
  const statuses = ['draft', 'sent', 'accepted', 'rejected', 'expired'];

  // Group offertes per klant op basis van customer_email — robuust want we
  // hebben (nog) geen FK customer_id op quotes. Email is in praktijk uniek
  // genoeg per reseller-scope.
  const quotesByEmail = new Map<string, ResellerQuote[]>();
  for (const q of quotes) {
    const key = (q.customer_email || '').trim().toLowerCase();
    if (!key) continue;
    const arr = quotesByEmail.get(key) ?? [];
    arr.push(q);
    quotesByEmail.set(key, arr);
  }
  const matchedKeys = new Set<string>();
  const orphanQuotes: ResellerQuote[] = [];

  const updateStatus = async (id: string, status: string) => {
    setUpdatingQuoteId(id);
    try {
      await resellerUpdateQuoteStatus(id, status);
      onRefresh();
    } catch (e) {
      alert((e as Error).message);
    } finally {
      setUpdatingQuoteId(null);
    }
  };

  // Reseller-scoped PDF: branding = reseller's eigen profiel (company, adres,
  // BTW, contact). isResellerRouted=true zorgt voor de "Powered by Het
  // Gevelsteenbedrijf"-strip + installer-note onderaan, identiek aan wat Filip
  // genereert via z'n admin dashboard.
  const downloadPdf = async (q: ResellerQuote) => {
    // v3.2.125 — Open print-venster SYNCHROON in click-handler context.
    // Async imports zouden anders de pop-up blocker triggeren.
    const targetWindow = window.open('', '_blank');
    if (!targetWindow) {
      alert('Pop-up blocker actief. Sta pop-ups toe voor deze site en probeer opnieuw.');
      return;
    }
    targetWindow.document.write('<html><body style="font-family:system-ui;padding:40px;color:#8a4423">Offerte aan het laden…</body></html>');
    const { printQuotePDF, OWNER_COMPANY } = await import('../lib/quotePdf');
    const company = {
      name:       (profile?.company_name || user?.company_name || OWNER_COMPANY.name).trim(),
      address:    (profile?.address || user?.address || '').trim(),
      postalCode: (profile?.postal_code || user?.postal_code || '').trim(),
      city:       (profile?.city || user?.city || '').trim(),
      country:    'België',
      btw:        (profile?.btw_number || user?.btw_number || '').trim(),
      email:      (profile?.contact_email || user?.email || '').trim(),
      phone:      (profile?.contact_phone || user?.phone || '').trim(),
      // v5 blok 7: geprinte offerte draagt het logo van de reseller.
      logo_url:   profile?.logo_url || undefined,
    };
    printQuotePDF({
      quote: {
        id: q.id,
        customer_name: q.customer_name,
        customer_email: q.customer_email,
        customer_phone: q.customer_phone,
        customer_btw_number: q.customer_btw_number,
        status: q.status,
        total_excl_vat: q.total_excl_vat,
        total_incl_vat: q.total_incl_vat,
        is_b2b: q.is_b2b,
        notes_nl: q.notes_nl,
        created_at: q.created_at,
        expires_at: q.expires_at,
        installation_address:     q.installation_address ?? null,
        installation_postal_code: q.installation_postal_code ?? null,
        installation_city:        q.installation_city ?? null,
      },
      company,
      isResellerRouted: true,
      t,
      targetWindow,
    }).catch((e: Error) => {
      try { targetWindow.close(); } catch { /* ignore */ }
      alert('PDF maken mislukte: ' + e.message);
    });
  };

  // V6 punt 23: bij het inloggen meteen zien wat al besteld is en wat nog
  // openstaat. 'Besteld' = aanvaarde offertes (of met bestelling); 'open' =
  // draft/sent en nog niet vervallen.
  const nu = Math.floor(Date.now() / 1000);
  const besteld = quotes.filter(q => q.status === 'accepted');
  const openstaand = quotes.filter(q => (q.status === 'draft' || q.status === 'sent')
    && (!q.expires_at || q.expires_at > nu));
  const somIncl = (list: ResellerQuote[]) => list.reduce((t, q) => t + (q.total_incl_vat || 0), 0);
  const euroKort = (n: number) => '€' + Math.round(n).toLocaleString('nl-BE');

  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden max-w-full">
      {/* V6 punt 23 — samenvatting bovenaan: al besteld vs. openstaand */}
      {/* Gsm-fix (19/8): min-w-0 zodat de vakken krimpen; korte labels op gsm */}
      <div className="px-4 sm:px-6 pt-4 grid grid-cols-2 gap-2 sm:gap-3">
        <div className="min-w-0 rounded-xl border border-green-200 bg-green-50 px-3 sm:px-4 py-2.5 sm:py-3">
          <p className="text-[10px] sm:text-[11px] font-bold text-green-800 uppercase tracking-wide truncate">✓ <span className="hidden sm:inline">Al </span>besteld</p>
          <p className="text-lg sm:text-xl font-bold text-green-900 truncate">{besteld.length} <span className="text-xs sm:text-sm font-semibold">· {euroKort(somIncl(besteld))}</span></p>
        </div>
        <div className="min-w-0 rounded-xl border border-amber-200 bg-amber-50 px-3 sm:px-4 py-2.5 sm:py-3">
          <p className="text-[10px] sm:text-[11px] font-bold text-amber-800 uppercase tracking-wide truncate">⏳ Openstaand<span className="hidden sm:inline">e offertes</span></p>
          <p className="text-lg sm:text-xl font-bold text-amber-900 truncate">{openstaand.length} <span className="text-xs sm:text-sm font-semibold">· {euroKort(somIncl(openstaand))}</span></p>
        </div>
      </div>
      <div className="px-4 sm:px-6 py-4 border-b border-gray-100 flex items-center justify-between gap-2 flex-wrap">
        <h2 className="text-lg font-bold text-[#1a1a1a] flex items-center gap-2">
          <Users size={18} className="text-[#c47a3a]" /> Klanten & Offertes
          <span className="px-2 py-0.5 bg-[#c47a3a]/10 text-[#c47a3a] text-xs font-semibold rounded-full">
            {customers.length} klant{customers.length === 1 ? '' : 'en'} · {quotes.length} offerte{quotes.length === 1 ? '' : 's'}
          </span>
        </h2>
        <div className="flex gap-2 flex-wrap">
          {/* v3.2.98 — Op Filip's verzoek 'Nieuwe klant' → 'Installateur
              aanbrengen' en 'Nieuwe offerte' → 'Leveringskosten'. Klanten
              en offertes komen automatisch binnen via postcode-routing
              vanuit het admin-systeem; resellers voegen enkel installatie-
              partner en leveringskosten toe. Handlers blijven voorlopig
              dezelfde (open create-customer/create-quote modal) — kan later
              vervangen worden door echte installateur/leveringskost-flows
              na overleg met Filip. */}
          <button
            onClick={() => setCreatingCustomer(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-[#c47a3a] text-[#c47a3a] hover:bg-[#c47a3a]/5 rounded-lg text-xs font-bold transition-colors"
            title="Voeg een installatiepartner toe voor deze klant"
          >
            <Plus size={14} /> Installateur aanbrengen
          </button>
          <button
            onClick={() => setCreatingQuote(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-[#c47a3a] text-[#c47a3a] hover:bg-[#c47a3a]/5 rounded-lg text-xs font-bold transition-colors"
            title="Voeg leveringskosten toe bij eventuele leveringen of installaties"
          >
            <Plus size={14} /> Leveringskosten
          </button>
          {/* v5.19: offerte samenstellen met meerdere stenen, ter plaatse met de klant */}
          <button
            onClick={openBuilder}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-[#c47a3a] hover:bg-[#b06930] text-white rounded-lg text-xs font-bold transition-colors shadow-sm"
            title="Maak een offerte met één of meerdere gevelstenen — de klant betaalt catalogusprijs, uw korting is uw marge"
          >
            <Plus size={14} /> Nieuwe offerte
          </button>
        </div>
      </div>

      {/* v5 blok 5: offerte overnemen — klant geeft zijn offertereferentie
           (staat op zijn offerte/in zijn account), handelaar zet ze op zijn
           naam. Prijzen veranderen niet; de korting is de marge. */}
      <div className="px-4 sm:px-6 py-3 bg-amber-50/60 border-b border-amber-100 flex items-center gap-2 flex-wrap">
        <span className="text-xs font-bold text-amber-900 uppercase tracking-wide">Offerte overnemen</span>
        <input
          value={claimRef}
          onChange={e => setClaimRef(e.target.value)}
          placeholder="Offertereferentie van de klant"
          className="flex-1 min-w-[220px] px-3 py-1.5 border border-amber-300 rounded-lg text-xs font-mono focus:outline-none focus:border-[#c47a3a] bg-white"
          onKeyDown={e => { if (e.key === 'Enter') void claimQuote(); }}
        />
        <button
          onClick={() => void claimQuote()}
          disabled={claiming || !claimRef.trim()}
          className="px-3 py-1.5 bg-amber-700 hover:bg-amber-800 disabled:opacity-50 text-white rounded-lg text-xs font-bold transition-colors"
        >
          {claiming ? 'Bezig…' : 'Op mijn naam zetten'}
        </button>
      </div>

      {/* v5.24: visuele offerte-builder — de klant staat naast u, dus u kiest
           de stenen zoals vooraan op de site: foto, klantprijs én uw netto
           per steen. Klik op een steen om hem toe te voegen (max. 5). */}
      {building && (
        <div className="mx-4 sm:mx-6 my-3 border-2 border-[#c47a3a]/40 rounded-xl p-3 space-y-3 bg-[#faf7f3]/70">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <input value={buildName} onChange={e => setBuildName(e.target.value)} placeholder="Klantnaam"
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm" />
            <input value={buildEmail} onChange={e => setBuildEmail(e.target.value)} placeholder="E-mail klant" type="email"
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm" />
          </div>

          {/* Gekozen stenen — met m² en regelbedragen */}
          {buildLines.length > 0 && (
            <div className="space-y-1.5">
              <p className="text-[10px] font-bold uppercase tracking-wide text-[#8a7e74]">In deze offerte ({buildLines.length}/5)</p>
              {buildLines.map((l, i) => {
                const pr = products.find(pp => pp.id === l.product_id);
                if (!pr) return null;
                return (
                  <div key={i} className="bg-white border border-gray-100 rounded-lg text-xs">
                    <div className="flex items-center gap-2 p-2">
                    <img src={`/products/${pr.slug}.jpg`} alt={pr.name} className="w-10 h-10 rounded object-cover flex-shrink-0" loading="lazy" />
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold truncate">{pr.name}</p>
                      <p className="text-[#8a7e74]">klant €{pr.catalog_price}/m² · <span className="text-green-700 font-semibold">uw netto €{pr.effective_price}/m²</span></p>
                    </div>
                    <input type="number" min="1" value={l.qty_m2}
                      onChange={e => setBuildLines(ls => ls.map((x, xi) => xi === i ? { ...x, qty_m2: parseFloat(e.target.value) || 0 } : x))}
                      className="w-16 px-2 py-1.5 border border-gray-200 rounded" title="Oppervlakte in m²" />
                    <span className="text-[#8a7e74]">m²</span>
                    <span className="font-bold text-[#c47a3a] w-20 text-right">{formatEuro(pr.catalog_price * l.qty_m2)}</span>
                    <button onClick={() => setBuildLines(ls => ls.filter((_, xi) => xi !== i))} className="text-red-600 font-bold px-1" title="Steen verwijderen">✕</button>
                    </div>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-2 pb-1.5 text-[11px] text-[#4a4a4a]">
                      <label className="flex items-center gap-1">Voeg
                        <select value={l.joint_width_mm} onChange={e => setBuildLines(ls => ls.map((x, xi) => xi === i ? { ...x, joint_width_mm: Number(e.target.value) } : x))}
                          className="px-1 py-0.5 border border-gray-200 rounded text-[11px]">
                          {[0, 5, 8, 10, 12, 15].map(w => <option key={w} value={w}>{w === 0 ? 'geen' : `${w} mm`}</option>)}
                        </select>
                      </label>
                      <label className="flex items-center gap-1">Hoeken
                        <input type="number" min={0} max={50} value={l.corner_count}
                          onChange={e => setBuildLines(ls => ls.map((x, xi) => xi === i ? { ...x, corner_count: Math.max(0, Math.min(50, Number(e.target.value) || 0)) } : x))}
                          className="w-11 px-1 py-0.5 border border-gray-200 rounded text-[11px] text-center" />
                      </label>
                      <label className="flex items-center gap-1 cursor-pointer">
                        <input type="checkbox" checked={l.include_glue}
                          onChange={e => setBuildLines(ls => ls.map((x, xi) => xi === i ? { ...x, include_glue: e.target.checked } : x))} /> Lijm
                      </label>
                      <label className="flex items-center gap-1 cursor-pointer">
                        <input type="checkbox" checked={l.include_grout}
                          onChange={e => setBuildLines(ls => ls.map((x, xi) => xi === i ? { ...x, include_grout: e.target.checked } : x))} /> Voegsel
                      </label>
                      {l.include_grout && l.joint_width_mm > 0 && (
                        <select value={l.grout_color} onChange={e => setBuildLines(ls => ls.map((x, xi) => xi === i ? { ...x, grout_color: e.target.value } : x))}
                          className="px-1 py-0.5 border border-gray-200 rounded text-[11px]">
                          {['grijs', 'wit', 'antraciet', 'zand'].map(c2 => <option key={c2} value={c2}>{c2}</option>)}
                        </select>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Visuele stenenkiezer — klik om toe te voegen */}
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wide text-[#8a7e74] mb-1.5">
              Kies gevelstenen — klik om toe te voegen {buildLines.length >= 5 && <span className="text-red-600">(maximum van 5 bereikt)</span>}
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 max-h-[300px] overflow-y-auto pr-1">
              {products.map(pp => {
                const inQuote = buildLines.some(l => l.product_id === pp.id);
                const full = buildLines.length >= 5;
                return (
                  <button
                    key={pp.id}
                    onClick={() => {
                      // v5.26: toggle — nogmaals klikken zet de steen weer af
                      if (inQuote) { setBuildLines(ls => ls.filter(l => l.product_id !== pp.id)); return; }
                      if (full) return;
                      setBuildLines(ls => [...ls, { product_id: pp.id, qty_m2: 50, joint_width_mm: 10, corner_count: 0, include_glue: true, include_grout: true, grout_color: 'grijs' }]);
                    }}
                    disabled={full && !inQuote}
                    className={`text-left rounded-lg border-2 overflow-hidden bg-white transition-all ${
                      inQuote ? 'border-[#c47a3a] ring-1 ring-[#c47a3a]/40' : full ? 'border-gray-100 opacity-40 cursor-not-allowed' : 'border-gray-100 hover:border-[#c47a3a]/50'
                    }`}
                    title={inQuote ? 'Klik om deze steen weer uit de offerte te halen' : `${pp.name} — klant €${pp.catalog_price}/m², uw netto €${pp.effective_price}/m²`}
                  >
                    <div className="relative">
                      <img src={`/products/${pp.slug}.jpg`} alt={pp.name} className="w-full h-16 object-cover" loading="lazy" />
                      {inQuote && <span className="absolute top-1 right-1 w-4 h-4 bg-[#c47a3a] text-white rounded-full text-[10px] font-bold flex items-center justify-center">✓</span>}
                    </div>
                    <div className="p-1.5">
                      <p className="text-[10px] font-semibold truncate">{pp.name}</p>
                      <p className="text-[10px] text-[#c47a3a] font-bold">€{pp.catalog_price}/m²</p>
                      <p className="text-[9px] text-green-700">netto €{pp.effective_price}/m²</p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Totalen — klant ziet mee wat hij betaalt; uw marge voor de
               kortingsdiscussie ter plaatse */}
          <div className="flex items-center justify-between flex-wrap gap-2 pt-1 border-t border-[#c47a3a]/20">
            <div className="text-xs space-x-3">
              <span>Klant betaalt (stenen): <b className="text-[#c47a3a]">{formatEuro(buildCatalogTotal)}</b> excl. · {formatEuro(buildCatalogTotal * 1.21)} incl.</span>
              <span className="text-[#8a7e74]">+ lijm/voegsel & snijverlies worden automatisch exact bijgerekend op de offerte</span>
              <span className="text-green-700">Uw netto-inkoop: <b>{formatEuro(buildNettoTotal)}</b></span>
              <span className="text-green-700">Uw marge: <b>{formatEuro(buildCatalogTotal - buildNettoTotal)}</b></span>
            </div>
            <button onClick={() => void submitBuild()} disabled={buildBusy || buildLines.length === 0}
              className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white rounded-lg text-xs font-bold">
              {buildBusy ? 'Bezig…' : 'Offerte aanmaken'}
            </button>
          </div>
        </div>
      )}

      <div className="px-4 sm:px-6 py-4 space-y-2">
        {customers.length === 0 && quotes.length === 0 ? (
          <div className="text-center py-12">
            <Users size={32} className="mx-auto mb-2 text-gray-300" />
            <p className="text-sm font-semibold text-[#1a1a1a]">Nog geen klanten of offertes</p>
            <p className="text-xs text-[#8a7e74] mt-1 mb-3">
              Begin met je eerste klant of maak meteen een offerte.
            </p>
            <div className="flex gap-2 justify-center">
              <button
                onClick={() => setCreatingCustomer(true)}
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-white border border-[#c47a3a] text-[#c47a3a] rounded-lg text-sm font-semibold"
              >
                <Plus size={14} /> Eerste klant
              </button>
              <button
                onClick={() => setCreatingQuote(true)}
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#c47a3a] hover:bg-[#b06930] text-white rounded-lg text-sm font-semibold"
              >
                <Plus size={14} /> Eerste offerte
              </button>
            </div>
          </div>
        ) : (
          customers.map(c => {
            const key = (c.user_email || '').trim().toLowerCase();
            const cQuotes = quotesByEmail.get(key) ?? [];
            if (key) matchedKeys.add(key);
            const isB2B = !!(c.btw_number || c.company_name);
            const isOpen = expandedId === c.id;

            return (
              <div key={c.id} className={`rounded-lg overflow-hidden max-w-full border transition-colors ${isOpen ? 'border-[#c47a3a] ring-1 ring-[#c47a3a]/30' : 'border-[#e6ded4]'}`}>
                <button
                  onClick={() => setExpandedId(isOpen ? null : c.id)}
                  className={`w-full min-w-0 max-w-full px-4 py-3 flex items-center justify-between transition-colors text-left ${isOpen ? 'bg-[#c47a3a]/10' : 'bg-[#faf7f3]/60 hover:bg-[#faf7f3]'}`}
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="w-9 h-9 rounded-full bg-[#c47a3a]/10 flex items-center justify-center text-[#c47a3a] font-bold text-sm flex-shrink-0">
                      {(c.user_name || '?').charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-[#1a1a1a] truncate">{c.user_name ?? '—'}</span>
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${isB2B ? 'bg-blue-50 text-blue-700' : 'bg-[#c47a3a]/10 text-[#8a4423]'}`}>
                          {isB2B ? 'B2B' : 'B2C'}
                        </span>
                        {/* v5.20: offerte maken voor déze klant, direct voorgevuld */}
                        <button
                          onClick={() => openBuilderFor(c.user_name ?? '', c.user_email ?? '')}
                          className="px-2 py-0.5 bg-[#c47a3a] hover:bg-[#a8643a] text-white rounded text-[10px] font-bold"
                          title="Nieuwe offerte voor deze klant — aan de prijzen die u van Filip kreeg"
                        >
                          + Offerte
                        </button>
                        {cQuotes.length > 0 && (
                          <span className="px-1.5 py-0.5 rounded bg-[#c47a3a]/10 text-[#c47a3a] text-[10px] font-bold">
                            {cQuotes.length} offerte{cQuotes.length === 1 ? '' : 's'}
                          </span>
                        )}
                      </div>
                      {(c.company_name || c.user_email) && (
                        <div className="text-xs text-[#8a7e74] truncate min-w-0 max-w-full">
                          {c.company_name && <span className="font-medium">{c.company_name} · </span>}
                          {c.user_email}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="text-xs text-[#8a7e74] flex items-center gap-2 ml-2 flex-shrink-0">
                    <span className="hidden sm:inline">{formatDate(c.created_at)}</span>
                    <span className={`transition-transform ${isOpen ? 'rotate-180' : ''}`}>▾</span>
                  </div>
                </button>

                {isOpen && (
                  <div className="border-t border-[#e6ded4] bg-[#faf7f3]/50 p-4 space-y-4">
                    {/* Klantgegevens */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-sm">
                      <div><span className="text-xs font-semibold text-[#8a7e74] uppercase tracking-wide">Telefoon: </span>{c.phone ?? '—'}</div>
                      <div><span className="text-xs font-semibold text-[#8a7e74] uppercase tracking-wide">BTW: </span>{c.btw_number ?? '—'}</div>
                      <div className="sm:col-span-2"><span className="text-xs font-semibold text-[#8a7e74] uppercase tracking-wide">Adres: </span>
                        {[c.address, c.postal_code, c.city].filter(Boolean).join(', ') || '—'}
                      </div>
                    </div>

                    {/* Offerte-historiek */}
                    <div>
                      <h4 className="text-xs font-bold text-[#8a7e74] uppercase tracking-wide mb-2 flex items-center gap-2">
                        <FileText size={12} /> Offertes ({cQuotes.length})
                      </h4>
                      {cQuotes.length === 0 ? (
                        <p className="text-xs text-[#8a7e74] italic py-2">Nog geen offertes voor deze klant.</p>
                      ) : (
                        <div className="space-y-1.5">
                          {cQuotes.map(q => (
                            <div key={q.id} className="bg-white border border-gray-100 rounded-lg p-3 flex items-center gap-3 flex-wrap">
                              <div className="flex-1 min-w-[180px]">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="font-mono text-[10px] text-[#8a7e74]">{q.id.slice(0, 8)}</span>
                                  <StatusBadge status={q.status} />
                                  <span className="text-xs text-[#8a7e74]">{formatDate(q.created_at)}</span>
                                </div>
                                <div className="text-sm font-semibold text-[#1a1a1a] mt-0.5">
                                  {formatEuro(q.total_incl_vat)}
                                  <span className="text-xs text-[#8a7e74] font-normal ml-2">
                                    excl. {formatEuro(q.total_excl_vat)}
                                  </span>
                                </div>
                              </div>
                              <select
                                value={q.status}
                                disabled={updatingQuoteId === q.id}
                                onChange={e => updateStatus(q.id, e.target.value)}
                                className="px-2 py-1.5 border border-gray-200 rounded text-xs focus:border-[#c47a3a] focus:outline-none disabled:opacity-50"
                              >
                                {statuses.map(s => <option key={s} value={s}>{t(`admin.status.${s}`)}</option>)}
                              </select>
                              <button
                                onClick={() => downloadPdf(q)}
                                className="flex items-center gap-1 px-2.5 py-1.5 bg-[#c47a3a] hover:bg-[#b06930] text-white rounded text-xs font-semibold"
                                title="Download offerte als PDF"
                              >
                                <FileText size={12} /> PDF
                              </button>
                              <button
                                onClick={() => void docAct(q.id, async () => {
                                  if (!confirm(`Klant akkoord? Offerte omzetten naar bestelling (${formatEuro(q.total_incl_vat)}).`)) return;
                                  const { resellerConvertQuoteToOrder } = await import('../lib/api');
                                  await resellerConvertQuoteToOrder(q.id);
                                })}
                                className="px-2 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded text-xs font-bold"
                                title="Klant gaat akkoord → bestelling; daarna kan u een factuur maken"
                              >
                                → Bestel
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}

        {/* Orphan quotes — offertes zonder gekoppelde klant (bv. quick-quote vanaf
            de webshop waar geen account werd aangemaakt). */}
        {(() => {
          for (const q of quotes) {
            const key = (q.customer_email || '').trim().toLowerCase();
            if (!key || !matchedKeys.has(key)) orphanQuotes.push(q);
          }
          if (orphanQuotes.length === 0) return null;
          return (
            <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50/70 p-3 sm:p-4">
              <h3 className="text-xs font-bold text-amber-800 uppercase tracking-wide mb-2 flex items-center gap-2">
                <AlertCircle size={12} /> Losstaande offertes ({orphanQuotes.length})
              </h3>
              <p className="text-[11px] text-[#8a7e74] mb-2">
                Offertes zonder geregistreerde klant — bv. quick-quotes vanaf de webshop.
              </p>
              <div className="space-y-1.5">
                {orphanQuotes.map(q => (
                  <div key={q.id} className="bg-amber-50/50 border border-amber-100 rounded-lg p-3 flex items-center gap-3 flex-wrap">
                    <div className="flex-1 min-w-[200px]">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-[#1a1a1a]">{q.customer_name}</span>
                        <StatusBadge status={q.status} />
                      </div>
                      <div className="text-xs text-[#8a7e74]">{q.customer_email} · {formatDate(q.created_at)}</div>
                      <div className="text-sm font-semibold text-[#1a1a1a] mt-0.5">{formatEuro(q.total_incl_vat)}</div>
                    </div>
                    <select
                      value={q.status}
                      disabled={updatingQuoteId === q.id}
                      onChange={e => updateStatus(q.id, e.target.value)}
                      className="px-2 py-1.5 border border-gray-200 rounded text-xs focus:border-[#c47a3a] focus:outline-none disabled:opacity-50"
                    >
                      {statuses.map(s => <option key={s} value={s}>{t(`admin.status.${s}`)}</option>)}
                    </select>
                    <button
                      onClick={() => downloadPdf(q)}
                      className="flex items-center gap-1 px-2.5 py-1.5 bg-[#c47a3a] hover:bg-[#b06930] text-white rounded text-xs font-semibold"
                    >
                      <FileText size={12} /> PDF
                    </button>
                              <button
                                onClick={() => void docAct(q.id, async () => {
                                  if (!confirm(`Klant akkoord? Offerte omzetten naar bestelling (${formatEuro(q.total_incl_vat)}).`)) return;
                                  const { resellerConvertQuoteToOrder } = await import('../lib/api');
                                  await resellerConvertQuoteToOrder(q.id);
                                })}
                                className="px-2 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded text-xs font-bold"
                                title="Klant gaat akkoord → bestelling; daarna kan u een factuur maken"
                              >
                                → Bestel
                              </button>
                  </div>
                ))}
              </div>
            </div>
          );
        })()}
      </div>

      {/* v5.19: eigen bestellingen & facturen van deze reseller */}
      {(rOrders.length > 0 || rInvoices.length > 0) && (
        <div className="px-4 sm:px-6 pb-5 space-y-4">
          {rOrders.length > 0 && (
            <div className="rounded-xl border border-blue-200 bg-blue-50/60 p-3 sm:p-4">
              <h3 className="text-xs font-bold text-blue-800 uppercase tracking-wide mb-2">📦 Mijn bestellingen ({rOrders.length})</h3>
              {rOrders.map(o => (
                <div key={o.id} className="bg-white border border-blue-100 rounded-xl p-3 mb-2 flex items-center justify-between gap-2 flex-wrap text-sm">
                  <span className="font-mono text-xs">#{o.order_number}</span>
                  <span className="truncate">{o.customer_name}</span>
                  <span className="text-xs text-[#8a7e74]">{o.status} · {o.payment_terms === 'vooraf' ? 'vooraf' : o.payment_terms.replace('_', ' ')}</span>
                  <button
                    onClick={() => void docAct(o.id + ':pay', async () => {
                      const { resellerUpdateOrder } = await import('../lib/api');
                      await resellerUpdateOrder(o.id, { payment_status: o.payment_status === 'betaald' ? 'open' : 'betaald' });
                    })}
                    disabled={docBusy === o.id + ':pay'}
                    className={`px-2 py-0.5 rounded text-xs font-bold ${o.payment_status === 'betaald' ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-800'}`}
                  >
                    {o.payment_status === 'betaald' ? '✓ Betaald' : 'Open'}
                  </button>
                  {(o.invoice_count ?? 0) === 0 && o.status !== 'geannuleerd' && (
                    <button
                      onClick={() => void docAct(o.id + ':fac', async () => {
                        const { resellerCreateInvoice } = await import('../lib/api');
                        await resellerCreateInvoice(o.id);
                      })}
                      className="px-2 py-1 bg-[#c47a3a] hover:bg-[#a8643a] text-white rounded text-xs font-bold"
                    >
                      Maak factuur
                    </button>
                  )}
                  <span className="font-bold">{formatEuro(o.total_incl_vat)}</span>
                </div>
              ))}
            </div>
          )}
          {rInvoices.length > 0 && (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-3 sm:p-4">
              <h3 className="text-xs font-bold text-emerald-800 uppercase tracking-wide mb-2">🧾 Mijn facturen ({rInvoices.length})</h3>
              {rInvoices.map(inv => (
                <div key={inv.id} className="bg-white border border-emerald-100 rounded-xl p-3 mb-2 flex items-center justify-between gap-2 flex-wrap text-sm">
                  <span className="font-mono text-xs">{(inv as { doc_type?: string }).doc_type === 'creditnota' ? 'CN' : 'FAC'}-{new Date(inv.created_at * 1000).getFullYear()}-{String(inv.invoice_number).padStart(4, '0')}</span>
                  <span className={`px-2 py-0.5 rounded text-xs font-bold ${inv.status === 'betaald' ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-800'}`}>{inv.status}</span>
                  <span className="font-bold">{formatEuro(inv.total_incl_vat)}</span>
                  {(inv as { doc_type?: string }).doc_type !== 'creditnota' && (
                    <button
                      onClick={() => void docAct(inv.id + ':cn', async () => {
                        if (!confirm('Creditnota maken? De factuur zelf blijft wettelijk bewaard; de creditnota vergoedt het volledige bedrag.')) return;
                        const { resellerCreateCreditNote } = await import('../lib/api');
                        await resellerCreateCreditNote(inv.id);
                      })}
                      className="px-2 py-1 bg-red-50 hover:bg-red-100 text-red-700 border border-red-200 rounded text-xs font-bold"
                    >
                      Creditnota
                    </button>
                  )}
                  <button onClick={() => void resellerInvoicePdf(inv)} className="px-2 py-1 bg-[#c47a3a] hover:bg-[#a8643a] text-white rounded text-xs font-bold">PDF</button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {creatingCustomer && (
        <CustomerCreateModal
          onClose={() => setCreatingCustomer(false)}
          onSaved={() => { setCreatingCustomer(false); onRefresh(); }}
        />
      )}
      {creatingQuote && (
        <CreateQuoteModal
          onClose={() => setCreatingQuote(false)}
          onCreated={() => { setCreatingQuote(false); onRefresh(); }}
        />
      )}
    </div>
  );
}

function CreateQuoteModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const { t } = useLanguage();
  const [products, setProducts] = useState<ResellerProduct[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    customer_postal_code: '',
    address: '',
    is_b2b: false,
    customer_btw_number: '',
    notes: '',
    // Werkadres — 'zelfde als factuuradres' is default true. Toggle uit om
    // een apart werkadres in te vullen (klant verbouwt elders dan z'n
    // domicilie / B2B levert op werf en niet op BTW-adres).
    installation_same_as_billing: true,
    installation_address: '',
    installation_postal_code: '',
    installation_city: '',
  });
  // V5.34: toebehoren per steen — zelfde keuzes als de calculator vooraan.
  type QItem = {
    product_id: string; qty_m2: number;
    joint_width_mm: number; corner_count: number;
    include_glue: boolean; include_grout: boolean; grout_color: string;
  };
  const NEW_ITEM: QItem = { product_id: '', qty_m2: 50, joint_width_mm: 10, corner_count: 0, include_glue: true, include_grout: true, grout_color: 'grijs' };
  const [items, setItems] = useState<QItem[]>([{ ...NEW_ITEM }]);

  useEffect(() => {
    void resellerGetProducts().then(setProducts).catch((e: Error) => setError(e.message));
  }, []);

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      const sameAddress = form.installation_same_as_billing;
      await resellerCreateQuote({
        customer_name: form.customer_name,
        customer_email: form.customer_email,
        customer_phone: form.customer_phone || null,
        customer_postal_code: form.customer_postal_code || null,
        address: form.address || null,
        installation_address:     sameAddress ? null : (form.installation_address || null),
        installation_postal_code: sameAddress ? null : (form.installation_postal_code || null),
        installation_city:        sameAddress ? null : (form.installation_city || null),
        is_b2b: form.is_b2b,
        customer_btw_number: form.customer_btw_number || null,
        notes: form.notes || null,
        items: items.filter(i => i.product_id && i.qty_m2 > 0),
      });
      onCreated();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl my-8" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <h3 className="text-lg font-bold text-[#1a1a1a]">{t('reseller.quotes.createTitle')}</h3>
          <button onClick={onClose} className="p-1 text-[#8a7e74] hover:text-[#1a1a1a]"><X size={18} /></button>
        </div>
        <div className="px-6 py-5 max-h-[70vh] overflow-y-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-xs font-semibold text-[#8a7e74] uppercase tracking-wide mb-1">{t('reseller.quotes.fName')}</label>
              <input value={form.customer_name} onChange={e => setForm(f => ({ ...f, customer_name: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#c47a3a]" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#8a7e74] uppercase tracking-wide mb-1">{t('reseller.quotes.fEmail')}</label>
              <input type="email" value={form.customer_email} onChange={e => setForm(f => ({ ...f, customer_email: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#c47a3a]" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#8a7e74] uppercase tracking-wide mb-1">{t('reseller.quotes.fPhone')}</label>
              <input value={form.customer_phone} onChange={e => setForm(f => ({ ...f, customer_phone: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#c47a3a]" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#8a7e74] uppercase tracking-wide mb-1">{t('reseller.quotes.fPostal')}</label>
              <input value={form.customer_postal_code} onChange={e => setForm(f => ({ ...f, customer_postal_code: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#c47a3a]" />
            </div>
            <div className="sm:col-span-2 flex items-center gap-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.is_b2b} onChange={e => setForm(f => ({ ...f, is_b2b: e.target.checked }))}
                  className="w-4 h-4 text-[#c47a3a] focus:ring-[#c47a3a]" />
                <span className="text-sm text-[#4a4a4a]">{t('reseller.quotes.fIsB2B')}</span>
              </label>
              {form.is_b2b && (
                <input placeholder="BE0123.456.789" value={form.customer_btw_number} onChange={e => setForm(f => ({ ...f, customer_btw_number: e.target.value }))}
                  className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#c47a3a]" />
              )}
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-semibold text-[#8a7e74] uppercase tracking-wide mb-1">
                {form.is_b2b ? 'Bedrijfsadres (factuuradres)' : 'Woonadres (factuuradres)'}
              </label>
              <input
                value={form.address}
                placeholder="Kerkstraat 1, 2000 Antwerpen"
                onChange={e => setForm(f => ({ ...f, address: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#c47a3a]"
              />
            </div>
          </div>

          {/* ─── Werkadres / installatie-adres ──────────────────────────────
               Default = zelfde als factuuradres. Toggle uit als de plaatsing
               op een ander adres gebeurt (verbouwing elders, B2B-werf). */}
          <div className="border border-amber-200 bg-amber-50/40 rounded-lg p-4 mb-4">
            <label className="flex items-center gap-2 cursor-pointer mb-2">
              <input
                type="checkbox"
                checked={form.installation_same_as_billing}
                onChange={e => setForm(f => ({ ...f, installation_same_as_billing: e.target.checked }))}
                className="w-4 h-4 text-[#c47a3a] focus:ring-[#c47a3a]"
              />
              <span className="text-sm font-semibold text-[#1a1a1a]">
                Werkadres = factuuradres
              </span>
            </label>
            <p className="text-[11px] text-[#8a7e74] mb-2 ml-6">
              Vink uit als de plaatsing op een ander adres dan het factuuradres gebeurt
              (bv. verbouwing elders, B2B-werf). Dit komt prominent op de offerte zodat
              er geen misverstanden zijn over waar geleverd / geplaatst wordt.
            </p>
            {!form.installation_same_as_billing && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-2">
                <div className="sm:col-span-3">
                  <label className="block text-[10px] font-semibold text-[#8a7e74] uppercase tracking-wide mb-1">Werkadres</label>
                  <input
                    value={form.installation_address}
                    placeholder="Bouwwerf 3"
                    onChange={e => setForm(f => ({ ...f, installation_address: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#c47a3a] bg-white"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-semibold text-[#8a7e74] uppercase tracking-wide mb-1">Postcode</label>
                  <input
                    value={form.installation_postal_code}
                    placeholder="2000"
                    onChange={e => setForm(f => ({ ...f, installation_postal_code: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#c47a3a] bg-white"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-[10px] font-semibold text-[#8a7e74] uppercase tracking-wide mb-1">Gemeente</label>
                  <input
                    value={form.installation_city}
                    placeholder="Antwerpen"
                    onChange={e => setForm(f => ({ ...f, installation_city: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#c47a3a] bg-white"
                  />
                </div>
              </div>
            )}
          </div>

          <div className="border-t border-gray-100 pt-4">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-semibold text-[#1a1a1a]">{t('reseller.quotes.items')}</h4>
              <button onClick={() => setItems(prev => prev.length >= 5 ? prev : [...prev, { ...NEW_ITEM }])}
                className="text-xs text-[#c47a3a] hover:underline flex items-center gap-1">
                <Plus size={12} /> {t('reseller.quotes.addItem')}
              </button>
            </div>
            <div className="space-y-2">
              {items.map((it, idx) => (
                <div key={idx} className="border border-gray-100 rounded-lg p-2 space-y-1.5">
                <div className="flex items-center gap-2">
                  <select value={it.product_id} onChange={e => {
                    const v = e.target.value;
                    setItems(prev => prev.map((p, i) => i === idx ? { ...p, product_id: v } : p));
                  }}
                    className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#c47a3a]">
                    <option value="">{t('reseller.quotes.selectProduct')}</option>
                    {products.map(p => (
                      <option key={p.id} value={p.id}>{p.name} — €{p.catalog_price}/m²</option>
                    ))}
                  </select>
                  <input type="number" step="0.5" min="0" value={it.qty_m2} onChange={e => {
                    const v = Number(e.target.value);
                    setItems(prev => prev.map((p, i) => i === idx ? { ...p, qty_m2: v } : p));
                  }}
                    className="w-24 px-3 py-2 border border-gray-200 rounded-lg text-sm text-right focus:outline-none focus:border-[#c47a3a]" />
                  <span className="text-xs text-[#8a7e74]">m²</span>
                  {items.length > 1 && (
                    <button onClick={() => setItems(prev => prev.filter((_, i) => i !== idx))}
                      className="p-1.5 text-red-600 hover:bg-red-50 rounded">
                      <X size={14} />
                    </button>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 pl-1 pb-1 text-xs text-[#4a4a4a]">
                  <label className="flex items-center gap-1">
                    Voeg
                    <select value={it.joint_width_mm} onChange={e => setItems(prev => prev.map((p, i) => i === idx ? { ...p, joint_width_mm: Number(e.target.value) } : p))}
                      className="px-1.5 py-1 border border-gray-200 rounded text-xs">
                      {[0, 5, 8, 10, 12, 15].map(w => <option key={w} value={w}>{w === 0 ? 'geen' : `${w} mm`}</option>)}
                    </select>
                  </label>
                  <label className="flex items-center gap-1">
                    Hoeken
                    <input type="number" min={0} max={50} value={it.corner_count}
                      onChange={e => setItems(prev => prev.map((p, i) => i === idx ? { ...p, corner_count: Math.max(0, Math.min(50, Number(e.target.value) || 0)) } : p))}
                      className="w-12 px-1.5 py-1 border border-gray-200 rounded text-xs text-center" />
                  </label>
                  <label className="flex items-center gap-1 cursor-pointer">
                    <input type="checkbox" checked={it.include_glue}
                      onChange={e => setItems(prev => prev.map((p, i) => i === idx ? { ...p, include_glue: e.target.checked } : p))} />
                    Lijm
                  </label>
                  <label className="flex items-center gap-1 cursor-pointer">
                    <input type="checkbox" checked={it.include_grout}
                      onChange={e => setItems(prev => prev.map((p, i) => i === idx ? { ...p, include_grout: e.target.checked } : p))} />
                    Voegsel
                  </label>
                  {it.include_grout && it.joint_width_mm > 0 && (
                    <select value={it.grout_color} onChange={e => setItems(prev => prev.map((p, i) => i === idx ? { ...p, grout_color: e.target.value } : p))}
                      className="px-1.5 py-1 border border-gray-200 rounded text-xs">
                      {['grijs', 'wit', 'antraciet', 'zand'].map(c2 => <option key={c2} value={c2}>{c2}</option>)}
                    </select>
                  )}
                  <span className="text-[10px] text-[#8a7e74]">snijverlies zit er automatisch bij</span>
                </div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-4">
            <label className="block text-xs font-semibold text-[#8a7e74] uppercase tracking-wide mb-1">{t('reseller.quotes.fNotes')}</label>
            <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={2}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#c47a3a]" />
          </div>
        </div>
        {error && <div className="px-6 py-2 bg-red-50 text-red-700 text-sm border-t border-red-100">{error}</div>}
        <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-2">
          <button onClick={onClose} disabled={saving}
            className="px-4 py-2 text-sm font-medium text-[#4a4a4a] bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50">
            {t('admin.profile.cancel')}
          </button>
          <button onClick={submit} disabled={saving || !form.customer_name || !form.customer_email}
            className="px-4 py-2 text-sm font-semibold bg-[#c47a3a] text-white rounded-lg hover:bg-[#a8643a] disabled:opacity-50 flex items-center gap-2">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            {t('reseller.quotes.create')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// CustomerCreateModal — reseller registreert een klant zonder offerte
// (lead die nog moet beslissen, of klant die later een offerte vraagt).
// ════════════════════════════════════════════════════════════════════════════

function CustomerCreateModal({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    postal_code: '',
    city: '',
    company_name: '',
    btw_number: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const update = <K extends keyof typeof form>(key: K, value: typeof form[K]) =>
    setForm(f => ({ ...f, [key]: value }));

  const validate = (): string | null => {
    if (form.name.trim().length < 2) return 'Naam minstens 2 tekens';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) return 'Ongeldig e-mailadres';
    // B2B-coherentie: als één van bedrijfsnaam/btw is ingevuld, beide vereist
    const hasCompany = form.company_name.trim().length > 0;
    const hasBtw     = form.btw_number.trim().length > 0;
    if (hasCompany !== hasBtw) return 'Vul bedrijfsnaam EN BTW-nummer in (of laat allebei leeg)';
    return null;
  };

  const save = async () => {
    const err = validate();
    if (err) { setError(err); return; }
    setSaving(true);
    setError(null);
    try {
      await resellerCreateCustomer({
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        address: form.address.trim() || null,
        postal_code: form.postal_code.trim() || null,
        city: form.city.trim() || null,
        company_name: form.company_name.trim() || null,
        btw_number: form.btw_number.trim() || null,
      });
      onSaved();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const Field = ({ label, value, onChange, placeholder, required, type = 'text' }: {
    label: string; value: string; onChange: (v: string) => void; placeholder?: string;
    required?: boolean; type?: string;
  }) => (
    <div>
      <label className="block text-xs font-bold text-[#8a7e74] uppercase tracking-wide mb-1">
        {label}{required && <span className="text-red-500 ml-1">*</span>}
      </label>
      <input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#c47a3a]"
      />
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl my-8" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between bg-gradient-to-r from-[#c47a3a]/10 to-amber-50">
          <div>
            <h3 className="text-lg font-bold text-[#1a1a1a] flex items-center gap-2">
              <Plus size={18} className="text-[#c47a3a]" />
              Nieuwe klant toevoegen
            </h3>
            <p className="text-xs text-[#8a7e74] mt-0.5">
              Registreer een nieuwe klant. Offertes maken kan later — of meteen via de Offertes-tab.
            </p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/60 rounded-lg" title="Sluiten">
            <X size={18} />
          </button>
        </div>
        <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-4 max-h-[70vh] overflow-y-auto">
          <Field label="Naam" value={form.name} onChange={v => update('name', v)} placeholder="Jan Janssens" required />
          <Field label="E-mail" value={form.email} onChange={v => update('email', v)} placeholder="jan@voorbeeld.be" type="email" required />
          <Field label="Telefoon" value={form.phone} onChange={v => update('phone', v)} placeholder="+32 4XX XX XX XX" type="tel" />
          <Field label="Adres" value={form.address} onChange={v => update('address', v)} placeholder="Kerkstraat 1" />
          <Field label="Postcode" value={form.postal_code} onChange={v => update('postal_code', v)} placeholder="2000" />
          <Field label="Gemeente" value={form.city} onChange={v => update('city', v)} placeholder="Antwerpen" />
          <div className="sm:col-span-2 bg-blue-50/50 border border-blue-200 rounded-lg p-3">
            <p className="text-[11px] font-semibold text-blue-900 uppercase tracking-wide mb-2">
              Heeft de klant een eigen zaak?
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Bedrijfsnaam" value={form.company_name} onChange={v => update('company_name', v)} placeholder="Bedrijf BV" />
              <Field label="BTW-nummer" value={form.btw_number} onChange={v => update('btw_number', v)} placeholder="BE0123.456.789" />
            </div>
            <p className="text-[10px] text-blue-700 mt-1.5">
              Vul alleen in als de klant namens een zaak bestelt — beide velden moeten dan ingevuld zijn voor een B2B-factuur.
            </p>
          </div>
        </div>
        {error && (
          <div className="px-5 py-2 bg-red-50 border-t border-red-100 text-red-700 text-sm flex items-center gap-2">
            <AlertCircle size={14} /> {error}
          </div>
        )}
        <div className="px-5 py-4 border-t border-gray-100 flex flex-col sm:flex-row gap-2 sm:justify-end bg-gray-50 rounded-b-2xl">
          <button
            onClick={onClose}
            disabled={saving}
            className="px-5 py-2.5 bg-white border border-gray-200 hover:bg-gray-50 text-[#4a4a4a] rounded-lg text-sm font-medium min-h-[44px]"
          >
            Annuleren
          </button>
          <button
            onClick={save}
            disabled={saving}
            className="flex items-center justify-center gap-1.5 px-5 py-2.5 bg-[#c47a3a] hover:bg-[#b06930] disabled:opacity-50 text-white rounded-lg text-sm font-semibold min-h-[44px]"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
            {saving ? 'Bezig met opslaan…' : 'Klant aanmaken'}
          </button>
        </div>
      </div>
    </div>
  );
}
